// Private Provider Actions
import { ppMockApi } from "../../services/ppMockApi";

// Action Types
export const PP_ACTIONS = {
  // Submissions
  SET_SUBMISSIONS: "PP_SET_SUBMISSIONS",
  ADD_SUBMISSION: "PP_ADD_SUBMISSION",
  UPDATE_SUBMISSION: "PP_UPDATE_SUBMISSION",
  SET_SUBMISSIONS_LOADING: "PP_SET_SUBMISSIONS_LOADING",

  // Workbench
  SET_WORKBENCH_DATA: "PP_SET_WORKBENCH_DATA",
  UPDATE_PAGE_STATUS: "PP_UPDATE_PAGE_STATUS",
  SET_WORKBENCH_LOADING: "PP_SET_WORKBENCH_LOADING",

  // Inspections
  SET_INSPECTION_TEMPLATES: "PP_SET_INSPECTION_TEMPLATES",
  ADD_INSPECTION_TEMPLATE: "PP_ADD_INSPECTION_TEMPLATE",
  UPDATE_INSPECTION_TEMPLATE: "PP_UPDATE_INSPECTION_TEMPLATE",
  SET_PERMIT_INSPECTIONS: "PP_SET_PERMIT_INSPECTIONS",
  UPDATE_INSPECTION: "PP_UPDATE_INSPECTION",
  SET_INSPECTIONS_LOADING: "PP_SET_INSPECTIONS_LOADING",

  // Finance
  SET_PAYOUTS: "PP_SET_PAYOUTS",
  UPDATE_PAYOUT: "PP_UPDATE_PAYOUT",
  SET_STATEMENTS: "PP_SET_STATEMENTS",
  SET_FINANCE_SUMMARY: "PP_SET_FINANCE_SUMMARY",
  SET_FINANCE_LOADING: "PP_SET_FINANCE_LOADING",

  // Notifications
  SET_NOTIFICATIONS: "PP_SET_NOTIFICATIONS",
  UPDATE_NOTIFICATION: "PP_UPDATE_NOTIFICATION",
  SET_NOTIFICATIONS_LOADING: "PP_SET_NOTIFICATIONS_LOADING",

  // Settings
  SET_PP_SETTINGS: "PP_SET_PP_SETTINGS",
  UPDATE_PP_SETTINGS: "PP_UPDATE_PP_SETTINGS",
  SET_SETTINGS_LOADING: "PP_SET_SETTINGS_LOADING",

  // UI State
  SET_ACTIVE_SUBMISSION: "PP_SET_ACTIVE_SUBMISSION",
  SET_SUBMISSION_FILTERS: "PP_SET_SUBMISSION_FILTERS",
  SET_ERROR: "PP_SET_ERROR",
  CLEAR_ERROR: "PP_CLEAR_ERROR",
};

// Action Creators
export const setSubmissionsLoading = (loading) => ({
  type: PP_ACTIONS.SET_SUBMISSIONS_LOADING,
  payload: loading,
});

export const setSubmissions = (submissions) => ({
  type: PP_ACTIONS.SET_SUBMISSIONS,
  payload: submissions,
});

export const addSubmission = (submission) => ({
  type: PP_ACTIONS.ADD_SUBMISSION,
  payload: submission,
});

export const updateSubmission = (submission) => ({
  type: PP_ACTIONS.UPDATE_SUBMISSION,
  payload: submission,
});

export const setError = (error) => ({
  type: PP_ACTIONS.SET_ERROR,
  payload: error,
});

export const clearError = () => ({
  type: PP_ACTIONS.CLEAR_ERROR,
});

// Thunk Actions
export const loadSubmissions =
  (filters = {}) =>
  async (dispatch, getState) => {
    try {
      dispatch(setSubmissionsLoading(true));
      dispatch(clearError());

      const submissions = await ppMockApi.getSubmissions(filters);
      dispatch(setSubmissions(submissions));

      return submissions;
    } catch (error) {
      console.error("Failed to load submissions:", error);
      dispatch(setError("Failed to load submissions"));
      throw error;
    } finally {
      dispatch(setSubmissionsLoading(false));
    }
  };

export const createSubmission =
  (submissionData) => async (dispatch, getState) => {
    try {
      dispatch(clearError());

      const newSubmission = await ppMockApi.createSubmission(submissionData);
      dispatch(addSubmission(newSubmission));

      return newSubmission;
    } catch (error) {
      console.error("Failed to create submission:", error);
      dispatch(setError("Failed to create submission"));
      throw error;
    }
  };

export const updateSubmissionAction =
  (id, updates) => async (dispatch, getState) => {
    try {
      dispatch(clearError());

      const updatedSubmission = await ppMockApi.updateSubmission(id, updates);
      dispatch(updateSubmission(updatedSubmission));

      return updatedSubmission;
    } catch (error) {
      console.error("Failed to update submission:", error);
      dispatch(setError("Failed to update submission"));
      throw error;
    }
  };

export const assignReviewer =
  (submissionId, reviewerName) => async (dispatch, getState) => {
    try {
      dispatch(clearError());

      const updatedSubmission = await ppMockApi.assignReviewer(
        submissionId,
        reviewerName
      );
      dispatch(updateSubmission(updatedSubmission));

      return updatedSubmission;
    } catch (error) {
      console.error("Failed to assign reviewer:", error);
      dispatch(setError("Failed to assign reviewer"));
      throw error;
    }
  };

export const sendReminderAction =
  (submissionId) => async (dispatch, getState) => {
    try {
      dispatch(clearError());

      const result = await ppMockApi.sendReminder(submissionId);
      return result;
    } catch (error) {
      console.error("Failed to send reminder:", error);
      dispatch(setError("Failed to send reminder"));
      throw error;
    }
  };

// Workbench Actions
export const setWorkbenchLoading = (loading) => ({
  type: PP_ACTIONS.SET_WORKBENCH_LOADING,
  payload: loading,
});

export const setWorkbenchData = (data) => ({
  type: PP_ACTIONS.SET_WORKBENCH_DATA,
  payload: data,
});

export const updatePageStatus = (pageUpdate) => ({
  type: PP_ACTIONS.UPDATE_PAGE_STATUS,
  payload: pageUpdate,
});

export const loadWorkbench = (submissionId) => async (dispatch, getState) => {
  try {
    dispatch(setWorkbenchLoading(true));
    dispatch(clearError());

    const workbenchData = await ppMockApi.getWorkbench(submissionId);
    dispatch(setWorkbenchData(workbenchData));

    return workbenchData;
  } catch (error) {
    console.error("Failed to load workbench:", error);
    dispatch(setError("Failed to load workbench data"));
    throw error;
  } finally {
    dispatch(setWorkbenchLoading(false));
  }
};

export const savePageCheck =
  (submissionId, pageId, status, comments, issues) =>
  async (dispatch, getState) => {
    try {
      dispatch(clearError());

      const result = await ppMockApi.savePageCheck(
        submissionId,
        pageId,
        status,
        comments,
        issues
      );
      dispatch(updatePageStatus({ pageId, status, comments, issues }));

      return result;
    } catch (error) {
      console.error("Failed to save page check:", error);
      dispatch(setError("Failed to save page check"));
      throw error;
    }
  };

export const approveSubmission =
  (submissionId, notes) => async (dispatch, getState) => {
    try {
      dispatch(clearError());

      const updatedSubmission = await ppMockApi.approveSubmission(
        submissionId,
        notes
      );
      dispatch(updateSubmission(updatedSubmission));

      return updatedSubmission;
    } catch (error) {
      console.error("Failed to approve submission:", error);
      dispatch(setError("Failed to approve submission"));
      throw error;
    }
  };

export const returnForCorrections =
  (submissionId, notes) => async (dispatch, getState) => {
    try {
      dispatch(clearError());

      const updatedSubmission = await ppMockApi.returnForCorrections(
        submissionId,
        notes
      );
      dispatch(updateSubmission(updatedSubmission));

      return updatedSubmission;
    } catch (error) {
      console.error("Failed to return for corrections:", error);
      dispatch(setError("Failed to return for corrections"));
      throw error;
    }
  };

export const markAllPagesCompliant =
  (submissionId) => async (dispatch, getState) => {
    try {
      dispatch(clearError());

      const result = await ppMockApi.markAllPagesCompliant(submissionId);

      // Reload workbench data to get updated page statuses
      await dispatch(loadWorkbench(submissionId));

      return result;
    } catch (error) {
      console.error("Failed to mark all pages compliant:", error);
      dispatch(setError("Failed to mark all pages compliant"));
      throw error;
    }
  };

// Inspection Actions
export const setInspectionsLoading = (loading) => ({
  type: PP_ACTIONS.SET_INSPECTIONS_LOADING,
  payload: loading,
});

export const setInspectionTemplates = (templates) => ({
  type: PP_ACTIONS.SET_INSPECTION_TEMPLATES,
  payload: templates,
});

export const addInspectionTemplate = (template) => ({
  type: PP_ACTIONS.ADD_INSPECTION_TEMPLATE,
  payload: template,
});

export const setPermitInspections = (permitId, inspections) => ({
  type: PP_ACTIONS.SET_PERMIT_INSPECTIONS,
  payload: { permitId, inspections },
});

export const updateInspection = (inspection) => ({
  type: PP_ACTIONS.UPDATE_INSPECTION,
  payload: inspection,
});

export const loadInspectionTemplates = () => async (dispatch, getState) => {
  try {
    dispatch(setInspectionsLoading(true));
    dispatch(clearError());

    const templates = await ppMockApi.getInspectionTemplates();
    dispatch(setInspectionTemplates(templates));

    return templates;
  } catch (error) {
    console.error("Failed to load inspection templates:", error);
    dispatch(setError("Failed to load inspection templates"));
    throw error;
  } finally {
    dispatch(setInspectionsLoading(false));
  }
};

export const saveInspectionTemplate =
  (template) => async (dispatch, getState) => {
    try {
      dispatch(clearError());

      const savedTemplate = await ppMockApi.saveInspectionTemplate(template);

      if (template.id) {
        dispatch(updateInspection(savedTemplate));
      } else {
        dispatch(addInspectionTemplate(savedTemplate));
      }

      return savedTemplate;
    } catch (error) {
      console.error("Failed to save inspection template:", error);
      dispatch(setError("Failed to save inspection template"));
      throw error;
    }
  };

export const loadPermitInspections =
  (permitId) => async (dispatch, getState) => {
    try {
      dispatch(setInspectionsLoading(true));
      dispatch(clearError());

      const inspections = await ppMockApi.getPermitInspections(permitId);
      dispatch(setPermitInspections(permitId, inspections));

      return inspections;
    } catch (error) {
      console.error("Failed to load permit inspections:", error);
      dispatch(setError("Failed to load permit inspections"));
      throw error;
    } finally {
      dispatch(setInspectionsLoading(false));
    }
  };

export const transitionInspection =
  (permitId, inspectionId, action, data) => async (dispatch, getState) => {
    try {
      dispatch(clearError());

      const result = await ppMockApi.transitionInspection(
        permitId,
        inspectionId,
        action,
        data
      );
      // Update the inspection in the store
      dispatch(updateInspection(result));

      return result;
    } catch (error) {
      console.error("Failed to transition inspection:", error);
      dispatch(setError("Failed to update inspection"));
      throw error;
    }
  };

// Finance Actions
export const setFinanceLoading = (loading) => ({
  type: PP_ACTIONS.SET_FINANCE_LOADING,
  payload: loading,
});

export const setPayouts = (payouts) => ({
  type: PP_ACTIONS.SET_PAYOUTS,
  payload: payouts,
});

export const updatePayout = (payout) => ({
  type: PP_ACTIONS.UPDATE_PAYOUT,
  payload: payout,
});

export const setStatements = (statements) => ({
  type: PP_ACTIONS.SET_STATEMENTS,
  payload: statements,
});

export const setFinanceSummary = (summary) => ({
  type: PP_ACTIONS.SET_FINANCE_SUMMARY,
  payload: summary,
});

export const loadPayouts = (dateRange) => async (dispatch, getState) => {
  try {
    dispatch(setFinanceLoading(true));
    dispatch(clearError());

    const payouts = await ppMockApi.getPayouts(dateRange);
    dispatch(setPayouts(payouts));

    return payouts;
  } catch (error) {
    console.error("Failed to load payouts:", error);
    dispatch(setError("Failed to load payouts"));
    throw error;
  } finally {
    dispatch(setFinanceLoading(false));
  }
};

export const markPayoutPaid = (payoutId) => async (dispatch, getState) => {
  try {
    dispatch(clearError());

    const updatedPayout = await ppMockApi.markPayoutPaid(payoutId);
    dispatch(updatePayout(updatedPayout));

    return updatedPayout;
  } catch (error) {
    console.error("Failed to mark payout as paid:", error);
    dispatch(setError("Failed to update payout"));
    throw error;
  }
};

export const loadStatements = () => async (dispatch, getState) => {
  try {
    dispatch(setFinanceLoading(true));
    dispatch(clearError());

    const statements = await ppMockApi.getStatements();
    dispatch(setStatements(statements));

    return statements;
  } catch (error) {
    console.error("Failed to load statements:", error);
    dispatch(setError("Failed to load statements"));
    throw error;
  } finally {
    dispatch(setFinanceLoading(false));
  }
};

export const loadFinanceSummary = () => async (dispatch, getState) => {
  try {
    dispatch(clearError());

    const summary = await ppMockApi.getFinanceSummary();
    dispatch(setFinanceSummary(summary));

    return summary;
  } catch (error) {
    console.error("Failed to load finance summary:", error);
    dispatch(setError("Failed to load finance summary"));
    throw error;
  }
};

// Notification Actions
export const setNotificationsLoading = (loading) => ({
  type: PP_ACTIONS.SET_NOTIFICATIONS_LOADING,
  payload: loading,
});

export const setNotifications = (notifications) => ({
  type: PP_ACTIONS.SET_NOTIFICATIONS,
  payload: notifications,
});

export const updateNotification = (notification) => ({
  type: PP_ACTIONS.UPDATE_NOTIFICATION,
  payload: notification,
});

export const loadNotifications = () => async (dispatch, getState) => {
  try {
    dispatch(setNotificationsLoading(true));
    dispatch(clearError());

    const notifications = await ppMockApi.getNotifications();
    dispatch(setNotifications(notifications));

    return notifications;
  } catch (error) {
    console.error("Failed to load notifications:", error);
    dispatch(setError("Failed to load notifications"));
    throw error;
  } finally {
    dispatch(setNotificationsLoading(false));
  }
};

export const markNotificationRead =
  (notificationId) => async (dispatch, getState) => {
    try {
      dispatch(clearError());

      const updatedNotification =
        await ppMockApi.markNotificationRead(notificationId);
      dispatch(updateNotification(updatedNotification));

      return updatedNotification;
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      dispatch(setError("Failed to update notification"));
      throw error;
    }
  };

// Settings Actions
export const setSettingsLoading = (loading) => ({
  type: PP_ACTIONS.SET_SETTINGS_LOADING,
  payload: loading,
});

export const setPpSettings = (settings) => ({
  type: PP_ACTIONS.SET_PP_SETTINGS,
  payload: settings,
});

export const loadPpSettings = () => async (dispatch, getState) => {
  try {
    dispatch(setSettingsLoading(true));
    dispatch(clearError());

    const settings = await ppMockApi.getPpSettings();
    dispatch(setPpSettings(settings));

    return settings;
  } catch (error) {
    console.error("Failed to load PP settings:", error);
    dispatch(setError("Failed to load settings"));
    throw error;
  } finally {
    dispatch(setSettingsLoading(false));
  }
};

export const savePpSettings = (settings) => async (dispatch, getState) => {
  try {
    dispatch(setSettingsLoading(true));
    dispatch(clearError());

    const savedSettings = await ppMockApi.savePpSettings(settings);
    dispatch(setPpSettings(savedSettings));

    return savedSettings;
  } catch (error) {
    console.error("Failed to save PP settings:", error);
    dispatch(setError("Failed to save settings"));
    throw error;
  } finally {
    dispatch(setSettingsLoading(false));
  }
};

// UI Actions
export const setActiveSubmission = (submission) => ({
  type: PP_ACTIONS.SET_ACTIVE_SUBMISSION,
  payload: submission,
});

export const setSubmissionFilters = (filters) => ({
  type: PP_ACTIONS.SET_SUBMISSION_FILTERS,
  payload: filters,
});
